/** Width × depth of something to lay out (metres). */
export interface LayoutSize {
  w: number;
  h: number;
}

/** Axis-aligned rectangle, top-left corner + size (metres). */
export interface LayoutRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface LayoutPoint {
  x: number;
  y: number;
}

export interface LayoutResult {
  /** Centre per input size, same order. */
  points: LayoutPoint[];
  /**
   * True when every entry got its own cell (no two overlap). False only when `bounds` is too
   * small for all of them: the rest is clamped into `bounds` and may overlap.
   */
  overlapFree: boolean;
}

/** Default spacing between laid-out objects (metres). */
export const LAYOUT_GAP = 0.15;
/** Upper bound of grid rings searched around the drop point (keeps degenerate input cheap). */
const MAX_RINGS = 60;
const EPS = 1e-9;

function clamp(v: number, lo: number, hi: number): number {
  return lo > hi ? (lo + hi) / 2 : Math.min(Math.max(v, lo), hi);
}

/** Centre `p` moved so a w × h box around it lies inside `bounds` (centred when it cannot). */
export function clampCentre(p: LayoutPoint, size: LayoutSize, bounds: LayoutRect): LayoutPoint {
  return {
    x: clamp(p.x, bounds.x + size.w / 2, bounds.x + bounds.w - size.w / 2),
    y: clamp(p.y, bounds.y + size.h / 2, bounds.y + bounds.h - size.h / 2),
  };
}

function inside(r: LayoutRect, b: LayoutRect): boolean {
  return (
    r.x >= b.x - EPS &&
    r.y >= b.y - EPS &&
    r.x + r.w <= b.x + b.w + EPS &&
    r.y + r.h <= b.y + b.h + EPS
  );
}

/** True when the two rectangles share some area (touching edges do not count). */
export function rectsOverlap(a: LayoutRect, b: LayoutRect): boolean {
  return (
    Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x) > EPS &&
    Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y) > EPS
  );
}

/** Rectangle of a w × h box centred on `p`. */
export function boxAt(p: LayoutPoint, size: LayoutSize): LayoutRect {
  return { x: p.x - size.w / 2, y: p.y - size.h / 2, w: size.w, h: size.h };
}

/** Grid offsets of ring `r` (Chebyshev distance r), nearest first, then row/column order. */
function ring(r: number): [number, number][] {
  const out: [number, number][] = [];
  for (let j = -r; j <= r; j++)
    for (let i = -r; i <= r; i++) if (Math.max(Math.abs(i), Math.abs(j)) === r) out.push([i, j]);
  return out.sort(
    (a, b) => a[0] * a[0] + a[1] * a[1] - (b[0] * b[0] + b[1] * b[1]) || a[1] - b[1] || a[0] - b[0],
  );
}

/**
 * Lays out objects around a drop point (OIL-37) without overlap: a grid of equal cells (largest
 * size + `gap`) centred on `at` – moved inside `bounds` first – filled ring by ring from the
 * middle. Cells must lie inside `bounds`; cells clear of `obstacles` (e.g. objects already on the
 * stage) are preferred. When `bounds` cannot hold every cell, the rest is clamped into `bounds`.
 */
export function layoutAround(
  at: LayoutPoint,
  sizes: readonly LayoutSize[],
  bounds: LayoutRect,
  opts: { gap?: number; obstacles?: readonly LayoutRect[] } = {},
): LayoutResult {
  if (sizes.length === 0) return { points: [], overlapFree: true };
  const gap = Math.max(0, opts.gap ?? LAYOUT_GAP);
  const max: LayoutSize = {
    w: Math.max(...sizes.map((s) => s.w)),
    h: Math.max(...sizes.map((s) => s.h)),
  };
  const cell = { w: max.w + gap, h: max.h + gap };
  const centre = clampCentre(at, max, bounds);
  const rings = Math.min(MAX_RINGS, Math.ceil(Math.max(bounds.w / cell.w, bounds.h / cell.h)) + 1);
  const obstacles = opts.obstacles ?? [];
  const cells: LayoutPoint[] = [];
  const clear: boolean[] = [];
  // Rings are generated only until every object has a clear cell: the result is the same (the
  // first clear cells in ring order win), but tiny objects on a big stage stay cheap.
  let free = 0;
  for (let r = 0; r <= rings && free < sizes.length; r++)
    for (const [i, j] of ring(r)) {
      const p = { x: centre.x + i * cell.w, y: centre.y + j * cell.h };
      const box = boxAt(p, max);
      if (!inside(box, bounds)) continue;
      const ok = !obstacles.some((o) => rectsOverlap(box, o));
      cells.push(p);
      clear.push(ok);
      if (ok) free += 1;
    }
  const used = new Set<number>();
  const take = (free: boolean): LayoutPoint | undefined => {
    const k = cells.findIndex((_, idx) => !used.has(idx) && (!free || clear[idx]));
    if (k < 0) return undefined;
    used.add(k);
    return cells[k];
  };
  let overlapFree = true;
  const points = sizes.map((size) => {
    const p = take(true) ?? take(false);
    if (p !== undefined) return p;
    overlapFree = false;
    return clampCentre(centre, size, bounds);
  });
  return { points, overlapFree };
}
